import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home, HelpCircle } from 'lucide-react';
import { logError } from '@/lib/errorLogger';
import { reportCrash } from '@/lib/crashReporter';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorType: 'network' | 'auth' | 'render' | 'unknown';
}

// Error type detection for better recovery guidance
function categorizeError(error: Error): 'network' | 'auth' | 'render' | 'unknown' {
  const message = error.message.toLowerCase();
  
  if (message.includes('fetch') || message.includes('network') || message.includes('failed to load')) {
    return 'network';
  }
  if (message.includes('auth') || message.includes('unauthorized') || message.includes('session')) {
    return 'auth';
  }
  if (message.includes('render') || message.includes('component') || message.includes('hook')) {
    return 'render';
  }
  return 'unknown';
}

// Recovery suggestions based on error type
const recoveryGuidance: Record<string, { title: string; steps: string[]; primaryAction: string }> = {
  network: {
    title: "Connection Issue",
    steps: [
      "Check your internet connection",
      "Try refreshing the page",
      "If the problem persists, wait a few minutes and try again"
    ],
    primaryAction: "Refresh Page"
  },
  auth: {
    title: "Session Expired",
    steps: [
      "Your login session may have expired",
      "Please sign in again to continue",
      "Your data is safe and will be available after signing in"
    ],
    primaryAction: "Sign In Again"
  },
  render: {
    title: "Display Error",
    steps: [
      "Something went wrong while displaying this page",
      "Try going back to the home screen",
      "If this keeps happening, please contact support"
    ],
    primaryAction: "Go Home"
  },
  unknown: {
    title: "Something Went Wrong",
    steps: [
      "An unexpected error occurred",
      "Try refreshing the page or returning home",
      "If this persists, please report it to help us improve"
    ],
    primaryAction: "Try Again"
  }
};

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorType: 'unknown',
  };

  public static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error,
      errorType: categorizeError(error)
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logError({
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack || "",
    });

    // Also send to remote crash log for cross-device telemetry
    void reportCrash({
      errorType: 'react_boundary',
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack || undefined,
      metadata: { errorType: this.state.errorType },
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorType: 'unknown' });

    try {
      window.history.pushState({}, "", "/dashboard");
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch {
      window.location.assign("/dashboard");
    }
  };

  private handlePrimaryAction = () => {
    const { errorType } = this.state;
    
    if (errorType === 'network') {
      window.location.reload();
    } else if (errorType === 'auth') {
      window.location.assign("/auth");
    } else {
      this.handleReset();
    }
  };

  public render() {
    if (this.state.hasError) {
      const { errorType, error } = this.state;
      const guidance = recoveryGuidance[errorType];

      return (
        <div className="min-h-screen bg-gradient-to-br from-background to-secondary/30 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <div className="flex items-center gap-2 text-destructive mb-2">
                <AlertTriangle className="h-6 w-6" aria-hidden="true" />
                <CardTitle>{guidance.title}</CardTitle>
              </div>
              <CardDescription>
                We've logged this issue and will look into it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Recovery Steps */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-primary" aria-hidden="true" />
                  What you can try:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                  {guidance.steps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ul>
              </div>

              {/* Error details (collapsed by default) */}
              {error && (
                <details className="group">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                    Technical details
                  </summary>
                  <div className="mt-2 p-3 bg-muted rounded-md text-xs font-mono break-all">
                    {error.message}
                  </div>
                </details>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-2">
                <Button 
                  onClick={this.handlePrimaryAction} 
                  className="flex-1 gap-2"
                >
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  {guidance.primaryAction}
                </Button>
                <Button 
                  onClick={this.handleReset} 
                  variant="outline"
                  className="flex-1 gap-2"
                >
                  <Home className="h-4 w-4" aria-hidden="true" />
                  Go Home
                </Button>
              </div>

              {/* Support link */}
              <p className="text-xs text-center text-muted-foreground pt-2">
                Need help?{" "}
                <a 
                  href="mailto:support@lupuslogix.com" 
                  className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                >
                  Contact Support
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
